import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useHasWriteRole } from '@/features/authentication/hooks/useHasWriteRole';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { authApi } from '@/services/authApi';
import {
  createSurveyFromTemplate,
  loadSurveyTemplates,
  SurveyTemplate,
} from '@/services/surveyApi';
import { loggingUtility } from '@/utils/loggingUtility';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { TieredMenu } from 'primereact/tieredmenu';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TopBar.css';

interface TopBarProps {
  onMenuClick: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const canCreate = useHasWriteRole();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const userMenuRef = useRef<Menu>(null);
  const createMenuRef = useRef<TieredMenu>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await loadSurveyTemplates();
        setTemplates(response.templates);
      } catch (error) {
        loggingUtility.error('Error loading templates:', error);
      }
    };
    void fetchTemplates();
  }, []);

  const handleCreateFromTemplate = useCallback(
    async (templateId: string) => {
      if (creatingFromTemplate) return;

      try {
        setCreatingFromTemplate(true);
        const response = await createSurveyFromTemplate(templateId);
        toast.showSuccess(t('common.success'), t('surveys.templateCreated'));
        navigate(`/surveys/edit/${response.surveyId}`);
      } catch (error) {
        loggingUtility.error('Error creating survey from template:', error);
        toast.showError(
          t('common.error'),
          t('surveys.errors.templateCreateFailed')
        );
      } finally {
        setCreatingFromTemplate(false);
      }
    },
    [creatingFromTemplate, navigate, t, toast]
  );

  const createMenuItems: MenuItem[] = [
    {
      label: t('projects.actions.newProject'),
      icon: 'pi pi-th-large',
      command: () => navigate('/projects/new'),
    },
    {
      label: t('layout.topbar.createWithAI'),
      icon: 'pi pi-sparkles',
      command: () => navigate('/surveys/new', { state: { mode: 'ai' } }),
    },
    {
      label: t('layout.topbar.createManually'),
      icon: 'pi pi-pencil',
      command: () => navigate('/surveys/new', { state: { mode: 'manual' } }),
    },
    ...(templates.length > 0
      ? [
          {
            label: t('layout.topbar.createSurveyFromTemplate'),
            icon: 'pi pi-box',
            items: templates.map(template => ({
              label: template.surveyName,
              command: () => void handleCreateFromTemplate(template.id),
            })),
          },
        ]
      : []),
  ];

  const handleLogout = async (): Promise<void> => {
    try {
      const logoutUrl = await authApi.logout();
      logout();
      window.location.href = logoutUrl;
    } catch {
      logout();
      navigate('/');
    }
  };

  const userMenuItems: MenuItem[] = [
    {
      label: t('layout.topbar.logout'),
      icon: 'pi pi-sign-out',
      command: () => handleLogout(),
    },
  ];

  const companyName =
    user?.organizations?.[0]?.name ||
    user?.organizations?.[0]?.slug ||
    t('layout.topbar.organization');

  return (
    <header className='flex items-center justify-between h-[60px] px-4 border-b border-surface'>
      <div className='flex items-center gap-4'>
        {isMobile && (
          <Button
            icon='pi pi-bars'
            onClick={onMenuClick}
            text
            aria-label={t('layout.topbar.toggleMenuAriaLabel')}
          />
        )}
        <span
          className='organization-name text-xl font-semibold'
          aria-label={t('voting.organizationName', { name: companyName })}
        >
          {companyName}
        </span>
      </div>
      <div className='flex items-center gap-3'>
        {canCreate && (
          <>
            <Button
              label={t('layout.topbar.create')}
              icon='pi pi-chevron-down'
              iconPos='right'
              onClick={e => createMenuRef.current?.toggle(e)}
              aria-label={t('layout.topbar.createAriaLabel')}
              data-testid='create-button-desktop'
              className='topbar-create-button !hidden sm:!inline-flex'
            />
            <Button
              icon='pi pi-chevron-down'
              onClick={e => createMenuRef.current?.toggle(e)}
              aria-label={t('layout.topbar.createAriaLabel')}
              className='topbar-create-button !inline-flex sm:!hidden'
              rounded
            />
            <TieredMenu
              model={createMenuItems}
              popup
              ref={createMenuRef}
              className='w-auto'
            />
          </>
        )}
        <Avatar
          icon='pi pi-user'
          shape='circle'
          className='cursor-pointer'
          onClick={e => userMenuRef.current?.toggle(e)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              userMenuRef.current?.toggle(e);
            }
          }}
          aria-label={t('layout.topbar.userMenuAriaLabel')}
          role='button'
          tabIndex={0}
        />
        <Menu model={userMenuItems} popup ref={userMenuRef} appendTo='self' />
      </div>
    </header>
  );
};
